import React, { useContext } from 'react';
import {
  Button,
  Callout,
  Card,
  Column,
  GridContainer,
  ModalDialog,
  ModalDialogBody,
  ModalDialogButtons,
  ModalDialogCloseButton,
  ModalDialogContent,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogNav,
  ModalDialogTitle,
  Row,
  Typography,
  useScreenClass,
} from '@vp/swan';
import { ReplaceTokens, useTranslations } from '@vp/om-translations';
import { MemoizedDocPreview } from './DocPreview';
import DesignFeedbackGroupRequestContext from '../../contexts/DesignFeedbackGroupRequestContext';
import { ExpertCard } from './ExpertCard';
import useSiteData from '../../../hooks/useSiteData';
import { EmbeddableLinkCreator } from '../../shared/EmbeddableLinkCreator';

export const OriginalDesignModal = ({ isModalOpen, setIsModalOpen }) => {
  const [designFeedbackMessages, sharedActionMessages] = useTranslations([
    'designFeedbackMessages',
    'sharedActionMessages',
  ]);

  const screenClass = useScreenClass();

  const { embroideryUrl } = useSiteData();
  const EmbroideryGuidelinesLink = EmbeddableLinkCreator({
    href: embroideryUrl,
    tabIndex: 0,
  });

  const { originalDesign, proposedDesignOptions } = useContext(
    DesignFeedbackGroupRequestContext
  );

  // we always show the first option only
  const proposedDesignOption = proposedDesignOptions[0];

  const onCloseHandler = () => {
    // Close modal
    setIsModalOpen(false);
  };

  const modalDisplayTitle = designFeedbackMessages.viewOriginalDesign;
  const modalContent = (
    <>
      <ModalDialogBody>
        <GridContainer>
          <Row pb={6}>
            <Column span={12}>
              <Typography mb={3}>
                {designFeedbackMessages.viewOriginalDesignMessage}
              </Typography>
              <ExpertCard
                title={designFeedbackMessages.expertEdits}
                messages={[
                  <ReplaceTokens
                    tokenizedString={
                      designFeedbackMessages.embroiderySimplification
                    }
                    substitutions={{
                      EmbroideryGuidelinesLink,
                    }}
                  />,
                ]}
              />
            </Column>
          </Row>
          <Row pb={6}>
            <Column span={6} mb={6}>
              <Card
                bordered
                style={{ width: '100%', height: '100%' }}
                key="doc-review-original-design"
              >
                <Callout m={0}>{designFeedbackMessages.labelOriginal}</Callout>
                <MemoizedDocPreview
                  previews={originalDesign.previews}
                  optionId={designFeedbackMessages.labelOriginal}
                  alt={designFeedbackMessages.labelOriginal}
                />
              </Card>
            </Column>

            <Column
              span={6}
              mb={6}
              key={`proposed-option-${proposedDesignOption.optionId}`}
            >
              <Card
                bordered
                style={{ width: '100%', height: '100%' }}
                key="doc-review-option"
              >
                <Callout m={0}>{designFeedbackMessages.labelNew}</Callout>
                <MemoizedDocPreview
                  previews={proposedDesignOption.previews}
                  optionId={proposedDesignOption.optionId}
                  alt={`${designFeedbackMessages.optionLabel}`}
                  vertical
                />
              </Card>
            </Column>
          </Row>
        </GridContainer>
      </ModalDialogBody>
      <ModalDialogFooter>
        <ModalDialogButtons>
          <GridContainer>
            <Row>
              <Column m="auto">
                <Button
                  skin="primary"
                  width={
                    ['xs'].includes(screenClass) ? 'full-width' : 'standard'
                  }
                  onClick={onCloseHandler}
                >
                  {sharedActionMessages.close}
                </Button>
              </Column>
            </Row>
          </GridContainer>
        </ModalDialogButtons>
      </ModalDialogFooter>
    </>
  );

  return (
    <ModalDialog
      isOpen={isModalOpen}
      onRequestDismiss={onCloseHandler}
      onlyRenderWhenOpen
    >
      <ModalDialogContent>
        <ModalDialogNav>
          <ModalDialogCloseButton accessibleText={sharedActionMessages.close} />
        </ModalDialogNav>
        <ModalDialogHeader>
          <ModalDialogTitle>{modalDisplayTitle}</ModalDialogTitle>
        </ModalDialogHeader>
        {modalContent}
      </ModalDialogContent>
    </ModalDialog>
  );
};
